import axios from "axios";
import React, { createContext, useState, useMemo } from "react";
import { API_ENDPOINT } from "../utils/constants";
import { extractDate, extractTime, toastError } from "../utils/util";
import * as XLSX from "xlsx";
import { getHeader } from "../utils/utils2";

export const ExportContext = createContext();

const MAX_CELL_LENGTH = 32767;
const EXPORT_PAGE_SIZE = 100;

const getResponseRows = (payload) => {
  if (Array.isArray(payload)) return payload;

  for (const key of ["data", "records", "items", "badges"]) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
};

const writeExcelFile = (rows, sheetName, fileName) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
};

const getCompanyName = (exhibitor) => {
  const companyName = exhibitor?.companyName;
  return typeof companyName === "string" ? companyName : companyName?.value;
};

const flattenStaffBadges = (payload) =>
  getResponseRows(payload).flatMap((record) => {
    const badges = Array.isArray(record?.badges) ? record.badges : [record];

    return badges.map((badge) => ({
      ...badge,
      printType: badge?.printType || record?.printType,
      createdOn:
        badge?.createdOn ||
        badge?.printedOn ||
        record?.createdOn ||
        record?.printedOn,
    }));
  });

export const ExportProvider = ({ children }) => {
  const [visitorExportType, setVisitorExportType] = useState(null);
  const [exhibitorExporting, setExhibitorExporting] = useState(false);
  const [tmExporting, setTmExporting] = useState(false);
  const [oesExporting, setOesExporting] = useState(false);
  const [productExporting, setProductExporting] = useState(false);
  const [serviceCodesExporting, setServiceCodesExporting] = useState(false);
  const [staffBadgesExporting, setStaffBadgesExporting] = useState(false);
  const [exhibitorLeadsExporting, setExhibitorLeadsExporting] = useState(false);

  const visitorExporting = visitorExportType !== null;

  const exportVisitors = async (type) => {
    try {
      setVisitorExportType(type);

      // 1. Fetch the binary Excel file from the backend
      // We MUST set responseType to 'blob' to handle the file stream correctly
      const config = {
        ...getHeader(),
        responseType: "blob",
      };

      const response = await axios.get(
        `${API_ENDPOINT}/admin/get-export-data/${encodeURIComponent(type)}`,
        config
      );

      // 2. Extract the filename from the Content-Disposition header if available
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${type}_export.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      // 3. Create a Blob from the binary response
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // 4. Create a temporary <a> tag to trigger the browser download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // 5. Cleanup the DOM and memory
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);

      // Handle the edge case where the backend throws an error (like 400 Bad Request)
      // Because we set responseType to 'blob', the JSON error message from the backend is also a blob.
      if (
        error.response &&
        error.response.data &&
        error.response.data instanceof Blob &&
        error.response.data.type === "application/json"
      ) {
        const reader = new FileReader();
        reader.onload = () => {
          const errorData = JSON.parse(reader.result);
          // Pass the parsed JSON error to your toast handler
          toastError({ response: { data: errorData } });
        };
        reader.readAsText(error.response.data);
      } else {
        toastError(error);
      }
    } finally {
      setVisitorExportType(null);
    }
  };

  const exportExhibitors = async () => {
    try {
      setExhibitorExporting(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/user/a/get-all-exhibitors`,
        getHeader()
      );

      const filteredData = data.map((item) => {
        const formattedString = (item.productCategories?.value || [])
          .map((cat) => `${cat.subSubCat}`)
          .join(", ");

        const aboutString = (item.about?.value || "").replace(
          /<\/?[^>]+(>|$)/g,
          ""
        );

        return {
          "Exhibitor ID": item.user_id,
          profile_image: item.profile_image,
          Username: item.username,
          Password: item.password,
          // Safe dynamic keys with fallbacks
          [item.companyName?.label || "Company Name"]: item.companyName?.value,
          [item.about?.label || "About"]: aboutString,
          [item.contactSalutation?.label || "Contact Salutation"]:
            item.contactSalutation?.value,
          [item.contactFirstName?.label || "First Name"]:
            item.contactFirstName?.value,
          [item.contactLastName?.label || "Last Name"]:
            item.contactLastName?.value,
          [item.contactDesignation?.label || "Designation"]:
            item.contactDesignation?.value,
          [item.mobile?.label || "Mobile"]: item.mobile?.value,
          [item.companyEmail?.label || "Company Email"]:
            item.companyEmail?.value,
          [item.contactEmail?.label || "Contact Email"]:
            item.contactEmail?.value,
          [item.address1?.label || "Address 1"]: item.address1?.value,
          [item.address2?.label || "Address 2"]: item.address2?.value,
          [item.address3?.label || "Address 3"]: item.address3?.value,
          [item.country?.label || "Country"]: item.country?.value?.name,
          [item.state?.label || "State"]: item.state?.value?.name,
          [item.city?.label || "City"]: item.city?.value?.name,
          [item.pin?.label || "Pin"]: item.pin?.value,
          [item.phone?.label || "Phone"]: item.phone?.value,
          [item.fax?.label || "Fax"]: item.fax?.value,
          [item.website?.label || "Website"]: item.website?.value,
          [item.companyShortName?.label || "Short Name"]:
            item.companyShortName?.value,
          [item.fascia?.label || "Fascia"]: item.fascia?.value,
          [item.weAre?.label || "We Are"]: item.weAre?.value,
          [item.industrySector?.label || "Industry Sector"]: (
            item.industrySector?.value || []
          )?.join(", "),
          [item.exhibitorCategory?.label || "Exhibitor Category"]:
            item.exhibitorCategory?.value,
          [item.productCategories?.label || "Product Categories"]:
            formattedString,
          [item.hallNumber?.label || "Hall Number"]: item.hallNumber?.value,
          [item.standNumber?.label || "Stand Number"]: item.standNumber?.value,
          [item.spaceType?.label || "Space Type"]: item.spaceType?.value,
          [item.exhibitArrival?.label || "Exhibit Arrival"]:
            item.exhibitArrival?.value,
          [item.exhibitArrivalTimeFrom?.label || "Exhibit Arrival Time From"]:
            item.exhibitArrivalTimeFrom?.value,
          [item.exhibitArrivalTimeTo?.label || "Exhibit Arrival Time To"]:
            item.exhibitArrivalTimeTo?.value,
          [item.materialArrival?.label || "Material Arrival"]:
            item.materialArrival?.value,
          [item.materialArrivalTimeFrom?.label || "Material Arrival Time From"]:
            item.materialArrivalTimeFrom?.value,
          [item.materialArrivalTimeTo?.label || "Material Arrival Time To"]:
            item.materialArrivalTimeTo?.value,
          [item.dismantling?.label || "Dismantling"]: item.dismantling?.value,
          [item.dismantlingTimeFrom?.label || "Dismantling Time From"]:
            item.dismantlingTimeFrom?.value,
          [item.dismantlingTimeTo?.label || "Dismantling Time To"]:
            item.dismantlingTimeTo?.value,
          [item.standSize?.label || "Stand Size"]: item.standSize?.value,
        };
      });

      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Exhibitors");
      XLSX.writeFile(wb, "exhibitors.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setExhibitorExporting(false);
    }
  };

  const exportProducts = async () => {
    try {
      setProductExporting(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/admin/get-all-products`,
        getHeader()
      );
      const filteredData = data?.map((item) => {
        // Added safe optional chaining to selectedCategories.value
        const myCats = (item.selectedCategories?.value || []).reduce(
          (acc, cat) => {
            const { mainCat, subSubCat } = cat;
            if (!acc[mainCat]) {
              acc[mainCat] = [];
            }
            acc[mainCat].push(subSubCat);
            return acc;
          },
          {}
        );
        const formattedString = Object.entries(myCats)
          .map(
            ([mainCat, subSubCats]) => `${mainCat}: ${subSubCats.join(", ")}`
          )
          .join(", ");

        return {
          "Object ID": item._id,
          Owner: item.owner,
          image: (item.images || []).join(","),
          name: item.name,
          categories: formattedString,
          description: (item.description || "").slice(0, 200),
          brochure: item.brochure || "",
          assignTo: item.assignTo || "",
          productVideo: item.productVideo?.content || "",
          Date: extractDate(item.createdOn),
          Time: extractTime(item.createdOn),
        };
      });
      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");
      XLSX.writeFile(wb, "products.xlsx");
    } catch (error) {
      console.log(error);
      toastError(error);
    } finally {
      setProductExporting(false);
    }
  };

  const exportOes = async () => {
    try {
      setOesExporting(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/admin/get-all-oes-submit`,
        getHeader()
      );

      const filteredData = data?.map((item) => {
        // Ensure stringified JSON doesn't crash the Excel file
        const rawFormData = item.form
          ? JSON.stringify(item.form)
          : item.user
          ? JSON.stringify(item.user)
          : "";

        const safeFormData =
          rawFormData.length > MAX_CELL_LENGTH
            ? rawFormData.substring(0, MAX_CELL_LENGTH - 3) + "..."
            : rawFormData;

        return {
          "Object ID": item._id,
          "Form Name": item.name,
          "Submitted By": item.submittedBy,
          "Submitted On": extractDate(item.updatedOn),
          Supplier: (item.selectedSupplier || item.supplier)?.supplierName,
          "Form Data": safeFormData,
        };
      });
      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "OES");
      XLSX.writeFile(wb, "oes.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setOesExporting(false);
    }
  };

  const exportTm = async () => {
    try {
      setTmExporting(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/admin/get-all-teammembers`,
        getHeader()
      );
      const filteredData = data?.map((item) => {
        return {
          "Object ID": item._id,
          Owner: item.owner,
          profile_image: item.profile_image || "",
          firstName: item.firstName,
          lastName: item.lastName,
          phone: item.phone,
          email: item.email,
          role: item.role,
          designation: item.designation,
          Date: extractDate(item.createdOn),
          Time: extractTime(item.createdOn),
        };
      });
      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Team Members");
      XLSX.writeFile(wb, "team_members.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setTmExporting(false);
    }
  };

  const exportServiceCodes = async () => {
    try {
      setServiceCodesExporting(true);

      const firstResponse = await axios.get(
        `${API_ENDPOINT}/admin/service-codes?page=1&limit=${EXPORT_PAGE_SIZE}`,
        getHeader()
      );
      const firstPageRows = getResponseRows(firstResponse.data);
      const total = Number(firstResponse.data?.total) || firstPageRows.length;
      const pageCount = Math.ceil(total / EXPORT_PAGE_SIZE);
      const remainingRequests = Array.from(
        { length: Math.max(0, pageCount - 1) },
        (_, index) =>
          axios.get(
            `${API_ENDPOINT}/admin/service-codes?page=${
              index + 2
            }&limit=${EXPORT_PAGE_SIZE}`,
            getHeader()
          )
      );
      const remainingResponses = await Promise.all(remainingRequests);
      const serviceCodes = [
        ...firstPageRows,
        ...remainingResponses.flatMap((response) =>
          getResponseRows(response.data)
        ),
      ];

      const rows = serviceCodes.map((item) => ({
        "Exhibitor ID": item.exhibitorId || "",
        "Company Name": item.companyName || "",
        "Hall Number": item.hallNumber || "",
        "Stand Number": item.standNumber || "",
      }));

      writeExcelFile(rows, "Service Codes", "service_codes.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setServiceCodesExporting(false);
    }
  };

  const exportExhibitorLeads = async () => {
    try {
      setExhibitorLeadsExporting(true);

      const [exhibitorsResponse, scannedResponse] = await Promise.all([
        axios.get(
          `${API_ENDPOINT}/admin/get-exhibitors-lookup-mini`,
          getHeader()
        ),
        axios.get(
          `${API_ENDPOINT}/admin/get-exhibitors-scanned-wise`,
          getHeader()
        ),
      ]);
      const companyLookup = new Map(
        getResponseRows(exhibitorsResponse.data).map((exhibitor) => [
          exhibitor.username,
          String(getCompanyName(exhibitor) || "").trim(),
        ])
      );
      const rows = getResponseRows(scannedResponse.data)
        .map((item) => {
          const count = Number(item.count);

          return {
            Username: item.username || "",
            "Company Name":
              companyLookup.get(item.username) || "Unknown company",
            "Leads Scanned": Number.isFinite(count) ? count : 0,
          };
        })
        .sort((a, b) => b["Leads Scanned"] - a["Leads Scanned"]);

      writeExcelFile(rows, "Exhibitor Leads", "exhibitor_leads.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setExhibitorLeadsExporting(false);
    }
  };

  const exportStaffBadges = async () => {
    try {
      setStaffBadgesExporting(true);

      const firstResponse = await axios.get(
        `${API_ENDPOINT}/admin/get-printed-staff-badges?page=1&limit=${EXPORT_PAGE_SIZE}`,
        getHeader()
      );
      const totalPages =
        Number(firstResponse.data?.totalPages) ||
        Math.ceil(
          (Number(firstResponse.data?.total) || 0) / EXPORT_PAGE_SIZE
        ) ||
        1;
      const remainingResponses = await Promise.all(
        Array.from({ length: Math.max(0, totalPages - 1) }, (_, index) =>
          axios.get(
            `${API_ENDPOINT}/admin/get-printed-staff-badges?page=${
              index + 2
            }&limit=${EXPORT_PAGE_SIZE}`,
            getHeader()
          )
        )
      );
      const staffBadges = [
        ...flattenStaffBadges(firstResponse.data),
        ...remainingResponses.flatMap((response) =>
          flattenStaffBadges(response.data)
        ),
      ];
      const rows = staffBadges.map((badge) => ({
        "Barcode ID": badge.barcode_id || badge.barcodeId || "",
        Name: badge.firstName || badge.name || "",
        Company: badge.companyName || badge.company || "",
        "Print Type": badge.printType || "",
        "Printed Date": extractDate(badge.createdOn),
        "Printed Time": extractTime(badge.createdOn),
      }));

      writeExcelFile(rows, "Staff Badges", "staff_badges.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setStaffBadgesExporting(false);
    }
  };

  // useMemo ensures the context object isn't recreated on every re-render
  // unless an actual exporting state changes.
  const contextValue = useMemo(
    () => ({
      exportVisitors,
      exportExhibitors,
      exhibitorExporting,
      visitorExporting,
      visitorExportType,
      exportProducts,
      productExporting,
      exportOes,
      oesExporting,
      exportTm,
      tmExporting,
      exportServiceCodes,
      serviceCodesExporting,
      exportStaffBadges,
      staffBadgesExporting,
      exportExhibitorLeads,
      exhibitorLeadsExporting,
    }),
    [
      exhibitorExporting,
      visitorExporting,
      visitorExportType,
      productExporting,
      oesExporting,
      tmExporting,
      serviceCodesExporting,
      staffBadgesExporting,
      exhibitorLeadsExporting,
    ]
  );

  return (
    <ExportContext.Provider value={contextValue}>
      {children}
    </ExportContext.Provider>
  );
};
